import type { CalendarOccurrence } from '@vision360/contracts';

/** Pure calendar layout: windows, day buckets, labels. Local time throughout. */

export type CalendarView = 'day' | 'week' | 'list';

const DAY_MS = 86_400_000;

export const startOfDay = (ms: number): Date => {
  const d = new Date(ms);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

/** Calendar days, so a DST change never shifts a boundary by an hour. */
export const addDays = (date: Date, days: number): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

/** What the view shows: day = today, week = 7 days from today, list = now → end of the n-th day. */
export function windowOf(view: CalendarView, days: number, now: number): [Date, Date] {
  const today = startOfDay(now);
  if (view === 'day') return [today, addDays(today, 1)];
  if (view === 'week') return [today, addDays(today, 7)];
  return [new Date(now), addDays(today, days)];
}

export const overlaps = (e: CalendarOccurrence, from: Date, to: Date): boolean =>
  Date.parse(e.start) < to.getTime() && Date.parse(e.end) > from.getTime();

/** Entries the view shows, sorted: all-day first within a start, then by start. */
export function inWindow(
  entries: readonly CalendarOccurrence[],
  view: CalendarView,
  days: number,
  now: number,
): CalendarOccurrence[] {
  const [from, to] = windowOf(view, days, now);
  return entries
    .filter((e) => overlaps(e, from, to))
    .sort(
      (a, b) =>
        Number(b.allDay) - Number(a.allDay) ||
        Date.parse(a.start) - Date.parse(b.start) ||
        a.summary.localeCompare(b.summary, 'fr'),
    );
}

export interface DayBucket {
  day: Date;
  allDay: CalendarOccurrence[];
  timed: CalendarOccurrence[];
}

/** One bucket per day from `from` for `count` days; an entry spanning days is in each. */
export function bucketByDay(
  entries: readonly CalendarOccurrence[],
  from: Date,
  count: number,
): DayBucket[] {
  return Array.from({ length: count }, (_, i) => {
    const day = addDays(from, i);
    const next = addDays(from, i + 1);
    const here = entries.filter((e) => overlaps(e, day, next));
    return {
      day,
      allDay: here.filter((e) => e.allDay),
      timed: here.filter((e) => !e.allDay),
    };
  });
}

/** Position of `ms` inside its day as a fraction of `[startHour, endHour)`, clamped. */
export function dayFraction(ms: number, day: Date, startHour: number, endHour: number): number {
  const hours = (ms - day.getTime()) / 3_600_000;
  return Math.min(1, Math.max(0, (hours - startHour) / (endHour - startHour)));
}

/** The hour span the week grid draws: 8 h – 20 h, widened to fit every timed entry. */
export function hourSpan(buckets: readonly DayBucket[]): [number, number] {
  let first = 8;
  let last = 20;
  for (const b of buckets) {
    for (const e of b.timed) {
      const s = Math.max(0, (Date.parse(e.start) - b.day.getTime()) / 3_600_000);
      const end = Math.min(24, (Date.parse(e.end) - b.day.getTime()) / 3_600_000);
      first = Math.min(first, Math.floor(s));
      last = Math.max(last, Math.ceil(end));
    }
  }
  return [first, last];
}

/** Side-by-side lanes for overlapping entries of one day (greedy, by start). */
export function laneOf(timed: readonly CalendarOccurrence[]): {
  byEntry: Map<CalendarOccurrence, number>;
  count: number;
} {
  const ends: number[] = [];
  const byEntry = new Map<CalendarOccurrence, number>();
  for (const e of [...timed].sort((a, b) => Date.parse(a.start) - Date.parse(b.start))) {
    const start = Date.parse(e.start);
    let lane = ends.findIndex((end) => end <= start);
    if (lane === -1) lane = ends.length;
    ends[lane] = Date.parse(e.end);
    byEntry.set(e, lane);
  }
  return { byEntry, count: Math.max(1, ends.length) };
}

export const timeFormat = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit' });
const dayLong = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
});
const dayShort = new Intl.DateTimeFormat('fr-FR', { weekday: 'short', day: 'numeric' });

export const shortDay = (day: Date): string => dayShort.format(day);

/** « Aujourd'hui », « Demain », else « mercredi 4 novembre ». */
export function dayTitle(
  day: Date,
  now: number,
  words: { today: string; tomorrow: string },
): string {
  const offset = Math.round((day.getTime() - startOfDay(now).getTime()) / DAY_MS);
  if (offset === 0) return words.today;
  if (offset === 1) return words.tomorrow;
  const text = dayLong.format(day);
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** "09:00–10:30", or the all-day word. */
export function timeRange(e: CalendarOccurrence, allDayWord: string): string {
  if (e.allDay) return allDayWord;
  return `${timeFormat.format(new Date(e.start))}–${timeFormat.format(new Date(e.end))}`;
}

/** The accessible name of an entry: « heure · titre · lieu ». */
export function entryLabel(e: CalendarOccurrence, allDayWord: string): string {
  return [timeRange(e, allDayWord), e.summary, e.location].filter(Boolean).join(' · ');
}
